// shared/src/schemas/signal-details.ts
// Phase 6 §K.6a (2026-05-25) — V2 narrow-signal `details` JSON adapter.
//
// Per docs/2026-05-25-flex-schema-brief-revision.md §K.6a — the api-worker
// is the SINGLE chokepoint for parsing the `signals.details` JSON sidecar.
// Routes + bundle assemblers call `unmarshalSignalDetails(row)` and hand
// typed objects to the frontend; zones never touch raw JSON.
//
// §K.6a-c (2026-05-25, task #48) — promoted from
// workers/api-worker/src/lib/signal-details.ts to @cf-apps/shared so the
// dashboard zone authors can import the SignalDetailsV2 type alongside the
// narrow Signal row type from one place. The unmarshal call site itself
// still lives in the api-worker boundary (routes + bundle assemblers);
// the schema definition belongs in shared because it's the read-side
// contract every consumer needs.
//
// `schema_version` per-row drives the case dispatch. V3 landed 2026-05-28
// (Session 5T D20 — PaceDial brief revision Phase A reader-side completion).
// V3 details shape DROPS V2.invalidation (folded into the row-level
// event_horizon_inv) and V2.catalyst_arbiter (collapsed into the row-level
// catalyst_label, which is also mirrored in V3 details for read convenience).
// All other V2 fields are preserved. Routes return SignalDetailsAny (V2 | V3);
// frontend switches on the discriminator via the schema_version column.
//
// Shape mirrors the writer side at workers/chain-workflow/src/stages/
// f.1.finalize.ts buildSignal `details` builder (state-only contract — no
// `action` field anywhere). `.passthrough()` keeps additive fields decodable
// without a schema bump.

import { z } from 'zod';

// 2026-06-22 — per-ticker STALE-FORWARD-CITE flags (twin of the macro
// SynthesisThesisV2.factual_error_flags). f.1.finalize buildSignal runs
// validateTickerForwardDates over details.catalyst / invalidation_trigger and
// attaches the result here; ZonePerTicker renders a "⚠ stale cite" badge when
// non-empty. Optional — absent/empty = clean. Shape mirrors shared FactualFlag.
const factualErrorFlagsSchema = z.array(z.object({
  field_path:  z.string(),
  stale_token: z.string(),
  stale_iso:   z.string(),
  today_iso:   z.string(),
  field_value: z.string(),
})).optional();

// ─── V2 Zod schema ───────────────────────────────────────────────────────

export const SignalDetailsV2Schema = z.object({
  // Arbiter prose + winning-refine mechanism prose.
  reasoning:              z.string(),
  mechanism:              z.string(),

  // Catalyst surface. catalyst (winning refine event prose) is "" when
  // path_type=TREND; catalyst_arbiter is the arbiter's own event prose
  // (often duplicates the refine but can refine the framing).
  catalyst:               z.string(),
  catalyst_arbiter:       z.string().optional(),
  catalyst_probability:   z.number().min(0).max(1).nullable(),

  // Invalidation surface. Two fields kept distinct for audit:
  //   invalidation         — arbiter's canonical falsifier (state-level)
  //   invalidation_trigger — winning refine's framing (legacy V1 name)
  invalidation:           z.string(),
  invalidation_trigger:   z.string(),

  // 2026-06-22 — per-ticker stale-forward-cite flags (see fragment above).
  factual_error_flags:    factualErrorFlagsSchema,

  // Cluster paradox prose (empty when no paradox).
  ticker_paradox:         z.string(),

  // V1-style key levels (code-derived from momentum's support/resistance).
  key_levels:             z.object({
    support:    z.array(z.number()),
    resistance: z.array(z.number()),
  }),

  // Code-derived momentum indicators (V1 cols moved to JSON per §K.6b).
  momentum:               z.object({
    rsi_14:         z.number().nullable(),
    macd_histogram: z.number().nullable(),
    rsi_14_trend:   z.enum(['rising', 'falling', 'flat']).nullable(),
  }),

  // Full refine outputs (passthrough so additive refine fields land here
  // with no schema bump). Frontend drill panel reads
  // contrarian_view.mechanism / .change_ci_low/high / etc.
  contrarian_view:        z.record(z.unknown()),
  consensus_view:         z.record(z.unknown()),

  // Arbiter weight split.
  weights:                z.object({
    contrarian: z.number().min(0).max(1),
    consensus:  z.number().min(0).max(1),
    inductive:  z.number().min(0).max(1).optional(),   // Option X (2026-06-06): present once the floor-gated p.inductive peer runs; 3 weights sum 1.0
  }),
  winning_side:           z.enum(['contrarian', 'consensus', 'inductive']),

  // Duplicated for read convenience — same value also indexed on
  // `signals.thesis_align`. Frontend can read either.
  thesis_align:           z.enum(['ALIGNED', 'DIVERGENT', 'NEUTRAL']).nullable(),
}).passthrough();

export type SignalDetailsV2 = z.infer<typeof SignalDetailsV2Schema>;

// ─── V3 Zod schema (PaceDial brief revision — Session 5T D20) ────────────
// Mirrors workers/chain-workflow/src/stages/f.1.finalize.ts:689-713 V3 builder.
// Diff vs V2:
//   - DROP `invalidation` (top-level arbiter falsifier collapsed to row.event_horizon_inv)
//   - DROP `catalyst_arbiter` (collapsed into row.catalyst_label)
//   - ADD  `catalyst_label` (mirror of row.catalyst_label for read convenience)
// All other fields preserved verbatim. `.passthrough()` keeps additive fields
// decodable without a schema bump (same pattern as V2).
//
// D25 (2026-05-28): Migration 0028's backfill set schema_version=3 on ALL
// rows including pre-V3 ones whose details JSON lacks `catalyst_label`.
// To read those rows without 500, catalyst_label defaults to '' when missing.
// Forward-emitted V3 rows always send a string (per the writer at
// f.1.finalize.ts:734); the default only kicks in for backfilled rows.

export const SignalDetailsV3Schema = z.object({
  reasoning:              z.string(),
  mechanism:              z.string(),

  // Catalyst surface. V3: catalyst (event prose from winning refine) is ""
  // when prediction_mode='trend'; catalyst_label (structured event name) is
  // also "" in trend mode. catalyst_probability is null in trend mode.
  catalyst:               z.string(),
  catalyst_label:         z.string().default(''),     // D25: backfilled rows lack this; new V3 writer always sends string
  catalyst_probability:   z.number().min(0).max(1).nullable(),

  // V3 keeps invalidation_trigger (winning refine's framing) but drops
  // the V2 `invalidation` top-level field (arbiter's canonical falsifier
  // is now expressed as row.event_horizon_inv price).
  invalidation_trigger:   z.string(),

  // Cluster paradox prose (empty when no paradox).
  ticker_paradox:         z.string(),

  // D40 (Session 5T 2026-05-28): pivot points replace the misleading
  // 30-day min/max named as support/resistance. Classic pivots from
  // PRIOR DAY OHLC: PP=(H+L+C)/3, R1=2*PP-L, S1=2*PP-H,
  // R2=PP+(H-L), S2=PP-(H-L). `pivot` is null when refresh-fetcher
  // had no prior bar (e.g., $AZTEC if cryptocom hadn't returned ≥2
  // klines yet). Legacy `support`/`resistance` arrays are kept
  // .optional() so pre-D40 V3 rows still parse; new writer omits them.
  key_levels:             z.object({
    pivot: z.object({
      pp: z.number(),
      s1: z.number(),
      s2: z.number(),
      r1: z.number(),
      r2: z.number(),
    }).nullable().optional(),
    support:    z.array(z.number()).optional(),       // legacy pre-D40 (30-day min)
    resistance: z.array(z.number()).optional(),       // legacy pre-D40 (30-day max)
  }),

  // Code-derived momentum indicators.
  momentum:               z.object({
    rsi_14:         z.number().nullable(),
    macd_histogram: z.number().nullable(),
    rsi_14_trend:   z.enum(['rising', 'falling', 'flat']).nullable(),
  }),

  // Full refine outputs (passthrough — see V2 note).
  contrarian_view:        z.record(z.unknown()),
  consensus_view:         z.record(z.unknown()),

  // Arbiter weight split.
  weights:                z.object({
    contrarian: z.number().min(0).max(1),
    consensus:  z.number().min(0).max(1),
    inductive:  z.number().min(0).max(1).optional(),   // Option X (2026-06-06): present once the floor-gated p.inductive peer runs; 3 weights sum 1.0
  }),
  winning_side:           z.enum(['contrarian', 'consensus', 'inductive']),

  // Duplicated for read convenience — same value also on row.thesis_align.
  thesis_align:           z.enum(['ALIGNED', 'DIVERGENT', 'NEUTRAL']).nullable(),

  // 2026-06-22 — per-ticker stale-forward-cite flags (see fragment above).
  factual_error_flags:    factualErrorFlagsSchema,
}).passthrough();

export type SignalDetailsV3 = z.infer<typeof SignalDetailsV3Schema>;

// Discriminated union exposed to read callers. Frontend uses row.schema_version
// to narrow. V2 + V3 fields are largely shared; the safest read path is to
// check the version when accessing V2-only `invalidation` or `catalyst_arbiter`.
export type SignalDetailsAny = SignalDetailsV2 | SignalDetailsV3;

// ─── unmarshal ───────────────────────────────────────────────────────────
// Per [[feedback-no-fallbacks]]: throw on unknown schema_version + on Zod
// parse failure. Callers MUST handle the throw or let it propagate as a
// 500 — a silent fallback would mask the blank-zone class of bug.

export interface SignalRowForAdapter {
  schema_version: number;
  details:        string;
}

export function unmarshalSignalDetails(row: SignalRowForAdapter): SignalDetailsAny {
  let parsed: unknown;
  try {
    parsed = JSON.parse(row.details);
  } catch (e) {
    throw new Error(
      `unmarshalSignalDetails: JSON.parse failed (schema_version=${row.schema_version}) — ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  switch (row.schema_version) {
    case 2:
      return SignalDetailsV2Schema.parse(parsed);
    case 3:
      return SignalDetailsV3Schema.parse(parsed);
    default:
      throw new Error(`unmarshalSignalDetails: unknown schema_version ${row.schema_version} (expected 2 or 3)`);
  }
}
